import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Sparkles, RefreshCw, TrendingUp, MapPin, AlertTriangle,
  DollarSign, Send, MessageSquare, Upload, X, ChevronRight, Zap,
  Clock, Users, Phone, Mail, Linkedin, CheckCircle2, CalendarClock,
  Search, FileText,
} from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { PageShell } from "@/components/altiora/PremiumUI";

const NAVY      = "#0B2545";
const NAVY_DEEP = "#081A33";
const CHAMPAGNE = "#C5A880";
const GOLD      = "#F5D687";
const GREEN     = "#0B7A3B";
const AMBER     = "#B45309";
const CRIMSON   = "#B91C1C";
const CREAM     = "#FBF6EA";

/* ══════════════════════════════════════════════════════════════════
   COPILOT HERO — the first thing the user sees
   ══════════════════════════════════════════════════════════════════ */
function CopilotHero({ recos, onSync, syncing, onChat, onUpload, onOpenInbox, onOpenNetwork, onOpenNavigator }) {
  if (!recos) return null;
  const c   = recos.counts || {};
  const ai  = recos.ai_narrative?.paragraph_fr;
  const pot = (recos.potential_value_cad || 0).toLocaleString("fr-CA");

  return (
    <div
      className="relative overflow-hidden rounded-2xl mb-8 shadow-2xl"
      style={{ background: `radial-gradient(1200px 400px at 20% 0%, #16406B 0%, ${NAVY} 45%, ${NAVY_DEEP} 100%)` }}
      data-testid="intel-copilote-hero"
    >
      {/* Grain overlay */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
           style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
      <div className="relative p-6 sm:p-8 text-white">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
               style={{ background: `linear-gradient(135deg,${GOLD} 0%,${CHAMPAGNE} 100%)` }}>
            <Sparkles size={20} style={{ color: NAVY }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: GOLD }}>
              Copilote ALTIORA · Synthèse du jour
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl leading-tight mt-1" data-testid="intel-copilote-headline">
              {recos.headline_fr}
            </h2>
          </div>
          <div className="hidden sm:flex gap-2 shrink-0">
            <button onClick={onOpenInbox} data-testid="intel-open-inbox-btn"
                    className="text-[12px] px-3 py-2 rounded-lg flex items-center gap-1.5 transition hover:brightness-110"
                    style={{ background: "rgba(245,214,135,0.15)", border: `1px solid ${GOLD}55`, color: GOLD }}>
              <FileText size={13} /> Boîte de réception
            </button>
            <button onClick={onOpenNetwork} data-testid="intel-open-network-btn"
                    className="text-[12px] px-3 py-2 rounded-lg flex items-center gap-1.5 transition hover:brightness-110"
                    style={{ background: "rgba(245,214,135,0.15)", border: `1px solid ${GOLD}55`, color: GOLD }}>
              <Users size={13} /> Réseau · Terrain
            </button>
            <button onClick={onOpenNavigator} data-testid="intel-open-navigator-btn"
                    className="text-[12px] px-3 py-2 rounded-lg flex items-center gap-1.5 transition hover:brightness-110"
                    style={{ background: "rgba(245,214,135,0.15)", border: `1px solid ${GOLD}55`, color: GOLD }}>
              <MapPin size={13} /> Navigateur
            </button>
            <button onClick={onChat} data-testid="intel-open-chat-btn"
                    className="text-[12px] px-3 py-2 rounded-lg flex items-center gap-1.5 transition hover:brightness-110"
                    style={{ background: "rgba(245,214,135,0.15)", border: `1px solid ${GOLD}55`, color: GOLD }}>
              <MessageSquare size={13} /> Parler au Copilote
            </button>
            <button onClick={onUpload} data-testid="intel-open-upload-btn"
                    className="text-[12px] px-3 py-2 rounded-lg flex items-center gap-1.5 transition hover:brightness-110"
                    style={{ background: "rgba(245,214,135,0.15)", border: `1px solid ${GOLD}55`, color: GOLD }}>
              <Upload size={13} /> Importer CSV
            </button>
            <button onClick={onSync} disabled={syncing} data-testid="intel-sync-btn"
                    className="text-[12px] px-3 py-2 rounded-lg flex items-center gap-1.5 transition disabled:opacity-50"
                    style={{ background: GOLD, color: NAVY, fontWeight: 700 }}>
              {syncing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              {syncing ? "Sync…" : "Synchroniser"}
            </button>
          </div>
        </div>

        {/* AI paragraph */}
        {ai && (
          <p className="text-[14px] sm:text-[15px] leading-relaxed opacity-95 mb-5 max-w-3xl"
             data-testid="intel-copilote-ai-paragraph">
            {ai}
          </p>
        )}

        {/* Micro-stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <HeroStat label="Nouveaux"        value={c.new_today ?? 0}  tid="hero-new" />
          <HeroStat label="Prioritaires"    value={c.priority ?? 0}   tid="hero-priority" accent={GOLD} />
          <HeroStat label="Partenaires"     value={c.partners ?? 0}   tid="hero-partners" />
          <HeroStat label="RDV à planifier" value={c.meetings ?? 0}   tid="hero-meetings" />
          <HeroStat label="Potentiel" value={`${pot} $`} tid="hero-potential" small />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 text-[12px] opacity-90">
          <span className="flex items-center gap-1"><Clock size={12} /> {recos.recommended_time_min} min recommandées aujourd'hui</span>
          {c.to_verify > 0 && (
            <span className="flex items-center gap-1"><AlertTriangle size={12} style={{ color: GOLD }} /> {c.to_verify} à qualifier</span>
          )}
          {recos.ai_narrative?.source === "ai_claude_sonnet_4_5" && (
            <span className="ml-auto text-[10px] uppercase tracking-widest opacity-70">Claude Sonnet 4.5</span>
          )}
        </div>

        {/* Mobile CTAs */}
        <div className="sm:hidden flex gap-2 mt-5">
          <button onClick={onChat} className="flex-1 text-[12px] py-2 rounded-lg" style={{ background: GOLD, color: NAVY, fontWeight: 700 }}>
            Parler au Copilote
          </button>
          <button onClick={onUpload} className="flex-1 text-[12px] py-2 rounded-lg" style={{ background: "rgba(245,214,135,0.15)", border: `1px solid ${GOLD}55`, color: GOLD }}>
            Importer
          </button>
        </div>
      </div>
    </div>
  );
}

function HeroStat({ label, value, tid, accent, small }) {
  return (
    <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }} data-testid={tid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className={`font-serif font-bold mt-0.5 ${small ? "text-lg" : "text-2xl"}`}
           style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   TIMELINE — today's actions with reasons + CTA
   ══════════════════════════════════════════════════════════════════ */
function ActionTimeline({ recos, onFeedback }) {
  const items = useMemo(() => {
    const out = [];
    (recos?.priority_actions || []).forEach(a => out.push({ ...a, bucket: "priority" }));
    (recos?.meetings_to_schedule || []).forEach(a => out.push({ ...a, bucket: "meeting" }));
    (recos?.to_verify || []).forEach(a => out.push({ ...a, bucket: "verify" }));
    return out.slice(0, 10);
  }, [recos]);

  if (!items.length) return null;

  const bucketMeta = {
    priority: { label: "Priorité", color: GREEN, icon: TrendingUp },
    meeting:  { label: "RDV",      color: CHAMPAGNE, icon: CalendarClock },
    verify:   { label: "Qualifier", color: AMBER, icon: AlertTriangle },
  };
  const channelIcon = { email: Mail, phone: Phone, linkedin: Linkedin };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 mb-6" data-testid="intel-timeline">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-serif text-xl" style={{ color: NAVY }}>Vos actions aujourd'hui</h3>
        <span className="text-[11px] uppercase tracking-widest" style={{ color: CHAMPAGNE }}>
          Timeline stratégique
        </span>
      </div>

      <div className="relative pl-6">
        {/* Timeline rail */}
        <div className="absolute left-2 top-2 bottom-2 w-px" style={{ background: `linear-gradient(${CHAMPAGNE}55, ${CHAMPAGNE}00)` }} />

        {items.map((it, idx) => {
          const meta = bucketMeta[it.bucket];
          const IconCh = channelIcon[it.channel] || Mail;
          const IconMeta = meta.icon;
          const done = it.status && ["contacted","meeting_scheduled","archived","verified"].includes(it.status);

          return (
            <div key={`${it.fingerprint}-${idx}`} className="relative pb-5 last:pb-0"
                 data-testid={`intel-timeline-item-${idx}`}>
              {/* Rail dot */}
              <div className="absolute -left-4 top-1.5 w-3 h-3 rounded-full ring-4 ring-white"
                   style={{ background: meta.color }} />

              <div className={`rounded-lg p-4 transition ${done ? "opacity-60" : ""}`}
                   style={{ background: idx === 0 ? "#FBF6EA" : "#F8FAFC",
                              border: `1px solid ${idx === 0 ? CHAMPAGNE + "55" : "#E5E7EB"}` }}>
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-[12px] shrink-0"
                       style={{ background: meta.color }}>
                    {Math.round(it.score)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[14px] font-semibold truncate" style={{ color: NAVY }}>
                        {it.name || "Contact"}
                      </span>
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                            style={{ color: meta.color, background: meta.color + "1A" }}>
                        <IconMeta size={9} className="inline mb-0.5" /> {meta.label}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      {it.sector_fr && <span>{it.sector_fr}</span>}
                      {it.city && <><span>·</span><span><MapPin size={9} className="inline" /> {it.city}</span></>}
                      <span>·</span>
                      <IconCh size={10} /> <span className="uppercase">{it.channel || "email"}</span>
                    </div>
                  </div>
                  {it.est_value_cad > 0 && (
                    <div className="text-right shrink-0">
                      <div className="text-[13px] font-bold" style={{ color: GREEN }}>
                        {(it.est_value_cad).toLocaleString("fr-CA")} $
                      </div>
                      <div className="text-[9px] text-slate-500 uppercase tracking-widest">Potentiel</div>
                    </div>
                  )}
                </div>

                {/* Reasons chips */}
                {it.reasons_fr?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 ml-12">
                    {it.reasons_fr.map((r, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 rounded-full"
                            style={{ background: NAVY + "0D", color: NAVY }}>
                        <ChevronRight size={9} className="inline -ml-0.5" /> {r}
                      </span>
                    ))}
                  </div>
                )}

                {/* Next action + CTA */}
                {it.next_action?.how && (
                  <div className="mt-3 ml-12 flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] italic text-slate-600 flex-1 min-w-[220px]">
                      → {it.next_action.how}
                    </span>
                    {!done && (
                      <>
                        <button onClick={() => onFeedback(it.fingerprint, "contacted")}
                                data-testid={`intel-cta-contact-${idx}`}
                                className="text-[11px] px-3 py-1.5 rounded-lg font-semibold transition hover:brightness-110"
                                style={{ background: meta.color, color: "#fff" }}>
                          {it.next_action.cta_label || "Contacter"}
                        </button>
                        <button onClick={() => onFeedback(it.fingerprint, "skipped")}
                                data-testid={`intel-cta-skip-${idx}`}
                                className="text-[11px] px-2 py-1.5 rounded-lg text-slate-500 hover:text-slate-700">
                          Reporter
                        </button>
                      </>
                    )}
                    {done && (
                      <span className="text-[11px] flex items-center gap-1" style={{ color: GREEN }}>
                        <CheckCircle2 size={12} /> {it.status === "skipped" ? "Reporté" : "Fait"}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   KPI GRID
   ══════════════════════════════════════════════════════════════════ */
function KpiCard({ icon: I, label, value, hint, tid, color }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4" data-testid={tid}>
      <div className="flex items-start justify-between">
        <I size={16} style={{ color: color || CHAMPAGNE }} />
      </div>
      <div className="font-serif text-2xl mt-2 font-bold" style={{ color: NAVY }}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-1">{label}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   CHAT DRAWER — free-form conversation with Copilote
   ══════════════════════════════════════════════════════════════════ */
function ChatDrawer({ open, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant",
      content: "Bonjour. Posez-moi une question sur vos opportunités du jour — je m'appuie sur vos données actuelles." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    const nextHist = [...messages, { role: "user", content: msg }];
    setMessages(nextHist);
    try {
      const r = await api.post("/intel/copilote/chat", {
        message: msg,
        history: messages.slice(-6),
      });
      setMessages([...nextHist, { role: "assistant", content: r.data.reply_fr }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur de communication avec le Copilote.";
      toast.error(detail);
      setMessages([...nextHist, { role: "assistant", content: detail }]);
    }
    setSending(false);
  };

  if (!open) return null;

  const suggestions = [
    "Quelle est ma priorité absolue ce matin ?",
    "Comment aborder Sébastien Leblanc ?",
    "Quels sont les risques que je ne vois pas ?",
    "Résume-moi le pipeline en 3 phrases.",
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end"
         data-testid="intel-chat-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:w-[440px] h-[80vh] sm:h-full bg-white shadow-2xl flex flex-col rounded-t-2xl sm:rounded-none">
        {/* Header */}
        <div className="p-4 flex items-center justify-between"
             style={{ background: `linear-gradient(120deg,${NAVY} 0%,${NAVY_DEEP} 100%)`, color: "#fff" }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                 style={{ background: GOLD }}>
              <Sparkles size={16} style={{ color: NAVY }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>Copilote ALTIORA</div>
              <div className="text-[14px] font-semibold">Conversation stratégique</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="intel-chat-close-btn"
                  className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center">
            <X size={16} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: CREAM }}>
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                 data-testid={`intel-chat-msg-${i}`}>
              <div className={`max-w-[86%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "text-white rounded-br-sm" : "rounded-bl-sm"}`}
                   style={{ background: m.role === "user" ? NAVY : "#fff",
                              color: m.role === "user" ? "#fff" : NAVY,
                              border: m.role === "user" ? "none" : `1px solid ${CHAMPAGNE}33` }}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="rounded-2xl px-3.5 py-2.5 text-[13px]"
                   style={{ background: "#fff", border: `1px solid ${CHAMPAGNE}33` }}>
                <Loader2 size={14} className="animate-spin inline mr-1" /> Le Copilote réfléchit…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Suggestions */}
        {messages.length <= 2 && (
          <div className="px-4 py-2 flex flex-wrap gap-1.5 border-t bg-white">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => setInput(s)}
                      data-testid={`intel-chat-suggestion-${i}`}
                      className="text-[11px] px-2.5 py-1 rounded-full transition hover:brightness-95"
                      style={{ background: CREAM, color: NAVY, border: `1px solid ${CHAMPAGNE}55` }}>
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="p-3 border-t bg-white flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)}
                 onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                 disabled={sending}
                 placeholder="Demandez au Copilote…"
                 data-testid="intel-chat-input"
                 className="flex-1 text-[13px] px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                 style={{ borderColor: CHAMPAGNE + "55" }} />
          <button onClick={send} disabled={sending || !input.trim()}
                  data-testid="intel-chat-send-btn"
                  className="px-3 rounded-lg disabled:opacity-50"
                  style={{ background: NAVY, color: "#fff" }}>
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   CSV UPLOAD MODAL — drag & drop
   ══════════════════════════════════════════════════════════════════ */
function UploadModal({ open, onClose, onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const inputRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setReport(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/intel/upload-csv", fd,
                                    { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      toast.success(`${r.data.imported} contact(s) importé(s)`);
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import impossible.");
    }
    setUploading(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         data-testid="intel-upload-modal">
      <div className="absolute inset-0 bg-black/50" onClick={() => !uploading && onClose()} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="p-5 flex items-center justify-between"
             style={{ background: `linear-gradient(120deg,${NAVY} 0%,${NAVY_DEEP} 100%)`, color: "#fff" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>Importer vos contacts</div>
            <h3 className="font-serif text-lg mt-0.5">Glissez votre fichier CSV</h3>
          </div>
          <button onClick={onClose} disabled={uploading} data-testid="intel-upload-close-btn"
                  className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center">
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          {!report ? (
            <>
              <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => {
                  e.preventDefault(); setDragging(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) doUpload(f);
                }}
                onClick={() => inputRef.current?.click()}
                data-testid="intel-upload-dropzone"
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${dragging ? "scale-[1.02]" : ""}`}
                style={{ borderColor: dragging ? GOLD : CHAMPAGNE + "88",
                            background: dragging ? CREAM : "#FAFAFA" }}
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 size={28} className="animate-spin" style={{ color: NAVY }} />
                    <div className="text-[13px]">Analyse en cours…</div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center"
                         style={{ background: CREAM }}>
                      <Upload size={22} style={{ color: NAVY }} />
                    </div>
                    <div className="text-[14px] font-semibold" style={{ color: NAVY }}>
                      Glissez ici ou cliquez pour choisir
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Formats : .csv, .txt (max 5 Mo)
                    </div>
                  </div>
                )}
                <input ref={inputRef} type="file" accept=".csv,.txt" className="hidden"
                       onChange={e => doUpload(e.target.files?.[0])}
                       data-testid="intel-upload-file-input" />
              </div>

              <div className="mt-4 text-[11px] text-slate-600">
                <div className="font-semibold mb-1" style={{ color: NAVY }}>Colonnes reconnues</div>
                <div className="flex flex-wrap gap-1.5">
                  {["nom","email","téléphone","secteur","ville"].map(c => (
                    <span key={c} className="px-2 py-0.5 rounded-full"
                          style={{ background: CREAM, color: NAVY, border: `1px solid ${CHAMPAGNE}33` }}>{c}</span>
                  ))}
                </div>
                <div className="mt-2 italic text-slate-500">
                  Fusion automatique par email / téléphone / nom pour éviter les doublons.
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-4" data-testid="intel-upload-report">
              <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
                   style={{ background: GREEN + "22" }}>
                <CheckCircle2 size={28} style={{ color: GREEN }} />
              </div>
              <h4 className="font-serif text-xl mt-3" style={{ color: NAVY }}>Import réussi</h4>
              <div className="text-[13px] text-slate-600 mt-1">{report.filename}</div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="p-3 rounded-lg" style={{ background: CREAM }}>
                  <div className="font-serif text-xl font-bold" style={{ color: NAVY }}>{report.parsed}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Analysés</div>
                </div>
                <div className="p-3 rounded-lg" style={{ background: GREEN + "1A" }}>
                  <div className="font-serif text-xl font-bold" style={{ color: GREEN }}>{report.imported}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Importés</div>
                </div>
                <div className="p-3 rounded-lg" style={{ background: AMBER + "1A" }}>
                  <div className="font-serif text-xl font-bold" style={{ color: AMBER }}>{report.skipped}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Ignorés</div>
                </div>
              </div>
              {report.sync?.new > 0 && (
                <div className="mt-3 text-[12px]" style={{ color: NAVY }}>
                  <TrendingUp size={12} className="inline mr-1" />
                  <b>+{report.sync.new}</b> nouvelle(s) opportunité(s) ajoutée(s) au tableau
                </div>
              )}
              <button onClick={onClose} data-testid="intel-upload-done-btn"
                      className="mt-5 px-5 py-2 rounded-lg text-[13px]"
                      style={{ background: NAVY, color: "#fff", fontWeight: 600 }}>
                Voir mes recommandations
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════════════ */
export default function IntelCommerciale() {
  const nav = useNavigate();
  const [recos, setRecos] = useState(null);
  const [data, setData] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const refresh = async () => {
    try {
      const [r, d, c] = await Promise.all([
        api.get("/intel/recommendations?enrich_ai=true"),
        api.get("/intel/dashboard"),
        api.get("/intel/connectors"),
      ]);
      setRecos(r.data); setData(d.data); setConnectors(c.data.connectors);
    } catch (e) {
      toast.error("Erreur de chargement.");
    }
  };
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/intel/sync");
      toast.success(`+${r.data.new} nouvelle(s) opportunité(s)`);
      await refresh();
    } catch { toast.error("Sync impossible."); }
    setSyncing(false);
  };

  const toggle = async (cid, on, status) => {
    if (status === "coming_soon") { toast.info("Ce connecteur arrive bientôt."); return; }
    try { await api.post(`/intel/connectors/${cid}/toggle`, { enabled: on }); await refresh(); }
    catch { toast.error("Impossible."); }
  };

  const handleFeedback = async (fingerprint, kind) => {
    try {
      await api.post("/intel/action-feedback", { fingerprint, kind });
      toast.success(kind === "contacted" ? "Contact marqué comme initié" : "Reporté");
      await refresh();
    } catch { toast.error("Impossible d'enregistrer."); }
  };

  if (loading) return (
    <PageShell testid="intel-loading">
      <TopBar />
      <div className="p-8 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
    </PageShell>
  );

  return (
    <PageShell testid="intel-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: CHAMPAGNE }}>
            ALTIORA · Intelligence Commerciale
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl mt-1" style={{ color: NAVY }} data-testid="intel-title">
            Votre Copilote stratégique
          </h1>
        </div>

        <CopilotHero recos={recos} onSync={sync} syncing={syncing}
                       onChat={() => setChatOpen(true)}
                       onUpload={() => setUploadOpen(true)}
                       onOpenInbox={() => nav("/app/inbox")}
                       onOpenNetwork={() => nav("/app/reseau")}
                       onOpenNavigator={() => nav("/app/navigateur")} />

        <ActionTimeline recos={recos} onFeedback={handleFeedback} />

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="intel-kpi-grid">
          <KpiCard icon={Zap}            label="Opportunités détectées" value={data?.total ?? 0}                        tid="kpi-total" />
          <KpiCard icon={Sparkles}       label="Nouvelles aujourd'hui"  value={data?.new_today ?? 0}                    tid="kpi-new"       color={GREEN} />
          <KpiCard icon={TrendingUp}     label="Prioritaires"           value={data?.priority_high ?? 0}                tid="kpi-priority"  color={CHAMPAGNE} />
          <KpiCard icon={AlertTriangle}  label="À vérifier"             value={data?.to_verify ?? 0}                    tid="kpi-verify"    color={AMBER} />
          <KpiCard icon={DollarSign}     label="Potentiel estimé"       value={`${(data?.potential_value_cad || 0).toLocaleString("fr-CA")} $`} tid="kpi-value" color={GREEN} />
          <KpiCard icon={Users}          label="RDV suggérés"           value={data?.meetings_suggested ?? 0}           tid="kpi-meetings" />
          <KpiCard icon={TrendingUp}     label="Taux de conversion"     value={`${data?.conversion_rate_pct ?? 0}%`}    tid="kpi-conv" />
          <KpiCard icon={AlertTriangle}  label="Ne pas contacter"       value={data?.do_not_contact ?? 0}               tid="kpi-donot"    color={CRIMSON} />
        </div>

        {/* Geo */}
        {data?.geo?.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-5 mb-6" data-testid="intel-geo">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={16} style={{ color: CHAMPAGNE }} />
              <h3 className="font-serif text-lg" style={{ color: NAVY }}>Répartition géographique</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {data.geo.map(g => (
                <div key={g.city} className="flex items-center justify-between p-2 rounded text-[12px]"
                     style={{ background: "#F1F5F9" }}>
                  <span>{g.city}</span>
                  <span className="font-bold" style={{ color: NAVY }}>{g.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Connecteurs */}
        <div className="bg-white rounded-lg shadow-sm p-5" data-testid="intel-connectors-panel">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-serif text-lg" style={{ color: NAVY }}>Sources de données</h3>
            <button onClick={() => setUploadOpen(true)}
                    data-testid="intel-open-upload-btn-2"
                    className="text-[12px] flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                    style={{ background: CREAM, color: NAVY, border: `1px solid ${CHAMPAGNE}55` }}>
              <FileText size={12} /> Importer CSV
            </button>
          </div>
          <p className="text-[12px] text-slate-500 mb-4">
            Activez les sources dont vous disposez. Chaque nouvelle source enrichit automatiquement le tableau.
          </p>
          <div className="space-y-2">
            {connectors.map(c => (
              <div key={c.id} className="flex items-start gap-3 p-3 rounded border"
                   style={{ borderColor: c.enabled ? CHAMPAGNE : "#E5E7EB",
                              background: c.enabled ? "#FBF6EA" : "#FFFFFF" }}
                   data-testid={`intel-connector-${c.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium" style={{ color: NAVY }}>{c.label_fr}</span>
                    {c.status === "coming_soon" && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#FEF3C7", color: AMBER }}>
                        Bientôt
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{c.description_fr}</div>
                </div>
                <button onClick={() => toggle(c.id, !c.enabled, c.status)}
                        disabled={c.status === "coming_soon"}
                        data-testid={`intel-toggle-${c.id}`}
                        className="text-[11px] px-3 py-1.5 rounded transition disabled:opacity-40"
                        style={{ background: c.enabled ? CHAMPAGNE : "#F1F5F9",
                                   color: c.enabled ? NAVY : "#334155", fontWeight: 600 }}>
                  {c.enabled ? "Activé" : "Activer"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)}
                     onUploaded={refresh} />
    </PageShell>
  );
}
